import React from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Typography from "../../../common/Typography";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import Button from "../../../common/Button";
import { SequenceSortContent } from "../../../../types/newtypes";
import { useSequenceSortHook } from "./hooks/useSequenceSortHook";
import { GripVertical } from "lucide-react";
import {
  DraggableItem,
  SequenceDropZone,
  FeedbackMessage,
} from "../ui-helpers";
import { useLessonDndSensors } from "./hooks/useLessonDndSensors";
import { useLessonDndAutoScroll } from "./hooks/useLessonDndAutoScroll";

interface SequenceSortPreviewProps {
  content: SequenceSortContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (...args: [boolean, boolean]) => void;
  cardId: string;
}

const SequenceSortPreview: React.FC<SequenceSortPreviewProps> = ({
  content,
  onCardStateChange,
  cardId,
}) => {
  const {
    droppedItems,
    activeId,
    isCorrect,
    showFeedback,
    feedbackMessage,
    handleDragStart,
    handleDragEnd,
    handleTryAgain,
    handleMoveToAvailable,
    getDraggableItems,
    getDropZones,
  } = useSequenceSortHook({
    content,
    onCardStateChange,
    cardId,
  });

  const sensors = useLessonDndSensors();
  const { startLessonDndAutoScroll, stopLessonDndAutoScroll } = useLessonDndAutoScroll();

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4">
        <Typography variant="h4" weight="bold">
          {content?.slide_title}
        </Typography>

        <div className="mb-8">
          <MarkdownWrapper content={content?.intro_text || ""} />
        </div>

        <MarkdownWrapper content={content?.prompt || ""} />
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        autoScroll={false}
        onDragStart={(event) => { startLessonDndAutoScroll(); handleDragStart(event); }}
        onDragEnd={(event) => { stopLessonDndAutoScroll(); handleDragEnd(event); }}
        onDragCancel={stopLessonDndAutoScroll}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Left Column - Available Items */}
          <div className="p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-2 mb-6">
              <GripVertical className="w-5 h-5 text-blue-400" />
              <Typography variant="h6" weight="bold" className="!text-blue-500">
                Available Items
              </Typography>
            </div>
            <div
              data-lesson-dnd-scroll-container
              data-testid="dnd-sequence-items-scroll-container"
              className="h-[400px] max-md:max-h-[50vh] overflow-y-auto bg-gray-50 p-4 rounded-lg"
            >
              <SortableContext
                items={getDraggableItems()?.map((item) => item?.id)}
                strategy={verticalListSortingStrategy}
              >
                {getDraggableItems()?.map((item) => (
                  <DraggableItem
                    key={item?.id}
                    id={item?.id}
                    item={item?.text}
                    isMatched={false}
                  />
                ))}
              </SortableContext>
            </div>
          </div>

          {/* Right Column - Drop Zones */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-2 mb-6">
              <Typography variant="h6" weight="bold" className="!text-blue-500">
                🎯 Arrange in Order
              </Typography>
            </div>
            <div
              data-lesson-dnd-scroll-container
              data-testid="dnd-sequence-drop-scroll-container"
              className="h-[400px] max-md:max-h-[50vh] overflow-y-auto"
            >
              <SortableContext
                items={getDropZones()?.map((zone) => zone?.id)}
                strategy={verticalListSortingStrategy}
              >
                {getDropZones()?.map((zone, index) => (
                  <SequenceDropZone
                    key={zone?.id}
                    id={zone?.id}
                    label={zone?.label}
                    matchedItem={zone?.matchedItem}
                    isCorrect={zone?.isCorrect}
                    isIncorrect={zone?.isIncorrect}
                    isLocked={zone?.isLocked}
                    stepNumber={index + 1}
                    isChecking={false}
                    isDisabled={zone?.isDisabled}
                    onMoveToAvailable={handleMoveToAvailable}
                    itemId={droppedItems[index]}
                  />
                ))}
              </SortableContext>
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeId ? (
            <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-6 py-4 rounded-xl shadow-2xl transform rotate-2">
              <div className="flex items-center gap-3">
                <GripVertical className="w-5 h-5 text-blue-200" />
                <Typography variant="p" className="text-white font-medium">
                  {content?.items?.find((item) => item?.id === activeId)?.text}
                </Typography>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Action Buttons */}
      <div className="mt-4 mb-4 flex gap-4">
        {!isCorrect && (
          <Button onClick={handleTryAgain} variant="filled" size="medium">
            Reset Incorrect
          </Button>
        )}
      </div>

      {/* Feedback Message */}
      {showFeedback && (
        <FeedbackMessage
          isSuccess={isCorrect}
          title={isCorrect ? "Great work!" : "Check the order"}
          message={feedbackMessage}
          showTryAgain={!isCorrect}
          onTryAgain={handleTryAgain}
        />
      )}
    </div>
  );
};

export default SequenceSortPreview;
